import type { ChangeEvent, VoidFunctionComponent } from 'react';
import { Component } from 'react';
import ReactTable from 'react-table';
import Toggle from 'react-toggle';
import type { ValueOf } from 'type-fest';
import { flagDescriptions, FlagType, flagTypeDescriptions } from '../../../shared/featureFlags/flagNames';
import { getAllExistingFlags, turnFlagOff, turnFlagOn } from '../../api/featureManager';
import { window } from '../../globals';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../generic/Button';
import Flex from '../generic/Flex';
import SearchInput from '../generic/SearchInput';
import Tooltip from '../generic/Tooltip';
import type { VisibleFlagName } from '../../../shared/featureFlags/types';
import { BadgeColors, BadgeShapes, TooltipBadge } from '../generic';
import { type DialogState, WithDialogContext } from '../../contexts/DialogContext';
import { getFeaturesForStaticPlan } from '../../../shared/subscriptions';

type FeatureControlCardProps = {
  setError(err: unknown): void;
  dialogContext: DialogState;
};

type OrgFlag = {
  id: VisibleFlagName;
  displayName: string;
  description: string;
  type: FlagType;
  isEnabled: boolean;
};

type SFeatureControlCardState = {
  flags: OrgFlag[];
  flagSearch: string;
  flagsToTurnOn: VisibleFlagName[];
  flagsToTurnOff: VisibleFlagName[];
};

// Flag colors are used to color the badges in the table
const FlagColors = {
  [FlagType.Beta]: BadgeColors.Blue,
  [FlagType.Deprecated]: BadgeColors.Red,
  [FlagType.Alpha]: BadgeColors.Yellow,
  [FlagType.Product]: BadgeColors.Green,
  [FlagType.Configuration]: BadgeColors.DarkGray,
  [FlagType.Tweaks]: BadgeColors.DarkGray,
  [FlagType.Customer]: BadgeColors.LightRed,
};

// Flag order is inferred from the order of the enum
// This assigns a number to determine order for the sort function
type FlagOrderType = Record<ValueOf<typeof FlagType>, number>;
const FlagOrder: FlagOrderType = Object.values(FlagType).reduce((acc, flagType, idx) => {
  acc[flagType] = idx;
  return acc;
}, {} as FlagOrderType);

class FeatureControlCard extends Component<FeatureControlCardProps, SFeatureControlCardState> {
  constructor(props: FeatureControlCardProps) {
    super(props);
    this.state = {
      flags: [],
      flagSearch: '',
      flagsToTurnOn: [],
      flagsToTurnOff: [],
    };
  }

  async componentDidMount() {
    const orgFlags = window.enabledFeatureFlags;
    const availableFlags = await getAllExistingFlags();
    const flags: OrgFlag[] = Object.entries(availableFlags)
      .map(([flag, { displayName, description, type }]) => ({
        id: flag as VisibleFlagName,
        displayName,
        description,
        type,
        isEnabled: orgFlags.some((orgFlag) => orgFlag.flag === flag),
      }))
      .sort(this.sortFlags);

    this.setState({ flags });
  }

  sortFlags = (a: OrgFlag, b: OrgFlag) => {
    const aPriority = FlagOrder[a.type];
    const bPriority = FlagOrder[b.type];

    if (aPriority === bPriority) {
      return a.displayName > b.displayName ? 1 : -1;
    }

    if (aPriority < bPriority) {
      return -1;
    }

    return 1;
  };

  toggleFlagOff = async (flag: VisibleFlagName) => {
    try {
      await turnFlagOff(flag);
      window.enabledFeatureFlags = window.enabledFeatureFlags.filter((ff) => ff.flag !== flag);
    } catch (err) {
      this.props.setError(err);
    }
  };

  setFlag = (isFlagSet: boolean) => (flag: VisibleFlagName): Promise<void> => {
    return isFlagSet ? this.toggleFlagOn(flag) : this.toggleFlagOff(flag);
  };

  saveFlags = async () => {
    const { flagsToTurnOff, flagsToTurnOn } = this.state;

    //  Let them update...
    await Promise.all([...flagsToTurnOff.map(this.setFlag(false)), ...flagsToTurnOn.map(this.setFlag(true))]);

    // ...then reload the page
    window.location.reload();
  };

  //  Handle clicks, allow us to show alerts/block bad or destructive actions.
  onSaveFlagsClick = () => {
    const { flagsToTurnOff, flagsToTurnOn } = this.state;

    if (!flagsToTurnOff.length && !flagsToTurnOn.length) {
      return;
    }

    const alphaFlagsToTurnOn = flagsToTurnOn.filter(
      (flag: VisibleFlagName) => flagDescriptions[flag]?.type === FlagType.Alpha,
    );

    const deprecatedFlagsToTurnOn = flagsToTurnOn.filter(
      (flag: VisibleFlagName) => flagDescriptions[flag]?.type === FlagType.Deprecated,
    );

    const areThereAlphaFlagsToTurnOn = alphaFlagsToTurnOn.length > 0;
    const areThereDeprecatedFlagsToTurnOn = deprecatedFlagsToTurnOn.length > 0;

    // Build message based on which flags are being enabled
    if (areThereAlphaFlagsToTurnOn || areThereDeprecatedFlagsToTurnOn) {
      let message = '';
      if (areThereAlphaFlagsToTurnOn && areThereDeprecatedFlagsToTurnOn) {
        message =
          'Some feature flags are still in development or are deprecated and may not work as expected or may not be supported.';
      } else if (areThereAlphaFlagsToTurnOn) {
        message = 'These feature flags are still in development and may not work as expected.';
      } else if (areThereDeprecatedFlagsToTurnOn) {
        message = 'These feature flags are deprecated and may not be supported any more.';
      }

      // Build list of flags + types
      const flagNames = [...alphaFlagsToTurnOn, ...deprecatedFlagsToTurnOn].map((flag) => [
        flagDescriptions[flag].displayName,
        flagDescriptions[flag].type,
      ]);

      const body = (
        <>
          {message} <b>Please contact Engineering before enabling these flags.</b>
          <br />
          <br />
          <ul>
            {flagNames.map(([displayName, type]) => (
              <li key={displayName}>
                {displayName} ({type})
              </li>
            ))}
          </ul>
        </>
      );
      this.props.dialogContext.onConfirm({
        title: '🤠 Hold up!',
        body,
        onConfirm: this.saveFlags,
        confirmText: 'I understand the risk',
        confirmBtnType: BUTTON_TYPE.ERROR,
      });
      return;
    }

    this.saveFlags();
  };

  toggleFlag = (flag: VisibleFlagName, isEnabled: boolean) => {
    try {
      this.setState((prevState) => {
        const flagsToTurnOff = [...prevState.flagsToTurnOff];
        const flagsToTurnOn = [...prevState.flagsToTurnOn];

        const offIndex = flagsToTurnOff.indexOf(flag);
        const onIndex = flagsToTurnOn.indexOf(flag);
        if (offIndex > -1) {
          flagsToTurnOff.splice(offIndex, 1);
        } else if (onIndex > -1) {
          flagsToTurnOn.splice(onIndex, 1);
        } else if (isEnabled) {
          flagsToTurnOn.push(flag);
        } else {
          flagsToTurnOff.push(flag);
        }

        return {
          flags: prevState.flags.map((ff) => (ff.id === flag ? { ...ff, isEnabled } : ff)),
          flagsToTurnOn,
          flagsToTurnOff,
        };
      });
    } catch (err) {
      this.props.setError(err);
    }
  };

  toggleFlagOn = async (flag: VisibleFlagName) => {
    try {
      await turnFlagOn(flag);

      if (!window.user?.organizationId) {
        throw new Error('No organizationId found for this user');
      }

      window.enabledFeatureFlags.push({
        flag,
        organizationId: window.user.organizationId,
      });
    } catch (err) {
      this.props.setError(err);
    }
  };

  getFeatureFlagColumns = () => [
    {
      id: 'id',
      accessor: 'id',
      show: false,
    },
    {
      id: 'displayName',
      accessor: 'displayName',
      Header: 'Flag Name',
      sortable: true,
      width: 200,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => (
        <Tooltip placement="top" tooltip={original.id} id={original.id}>
          <p>{original.displayName}</p>
        </Tooltip>
      ),
    },
    {
      id: 'description',
      accessor: 'description',
      Header: 'Description',
      style: { whiteSpace: 'unset' }, // Do not trim description for flags, wrap text instead.
    },
    {
      id: 'type',
      accessor: 'type',
      Header: 'Type',
      width: 100,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => {
        const { id, type } = original;
        return (
          <TooltipBadge
            color={FlagColors?.[type] || BadgeColors.LightGray}
            shape={BadgeShapes.Circle}
            id={id as string}
            tooltip={flagTypeDescriptions[type]}
          >
            {type}
          </TooltipBadge>
        );
      },
    },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: '',
      width: 60,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => {
        const { id, isEnabled } = original;
        return <Toggle checked={isEnabled} onChange={() => this.toggleFlag(id, !isEnabled)} icons={false} />;
      },
    },
  ];

  getIncludesSearch = (search: string) => {
    const searchLowerCase = search.toLowerCase();
    return (value: string) => value.toLowerCase().includes(searchLowerCase);
  };

  getFeatureFlagData = () =>
    this.state.flags.filter((f) => {
      if (!this.state.flagSearch) {
        return true;
      }

      const includesSearch = this.getIncludesSearch(this.state.flagSearch);
      return [f.displayName, f.id, f.description, f.type].some(includesSearch);
    });

  onFlagSearchChange = (event: ChangeEvent<HTMLInputElement>) => this.setState({ flagSearch: event.target.value });

  render() {
    const isSaveDisabled = !this.state.flagsToTurnOff.length && !this.state.flagsToTurnOn.length;
    return (
      <FeatureFlagControlCard
        flagSearch={this.state.flagSearch}
        onFlagSearchChange={this.onFlagSearchChange}
        onSaveFlagsClick={this.onSaveFlagsClick}
        isSaveDisabled={isSaveDisabled}
        featureFlagColumns={this.getFeatureFlagColumns()}
        featureFlagData={this.getFeatureFlagData()}
      />
    );
  }
}

type FeatureFlagControlCardProps = {
  flagSearch: string;
  onFlagSearchChange(event: ChangeEvent<HTMLInputElement>): void;
  onSaveFlagsClick(): void;
  isSaveDisabled: boolean;
  featureFlagColumns: any[];
  featureFlagData: OrgFlag[];
};

const FeatureFlagControlCard: VoidFunctionComponent<FeatureFlagControlCardProps> = ({
  flagSearch,
  onFlagSearchChange,
  onSaveFlagsClick,
  isSaveDisabled,
  featureFlagColumns,
  featureFlagData,
}) => {
  const tierFeatures = getFeaturesForStaticPlan(window.user?.organization?.stripe_plan);
  const flagData = featureFlagData.filter(({ id }) => !tierFeatures.has(id));
  return (
    <div className="card">
      <h3 className="nomargin-v margin-bottom-md">Organization Feature Control</h3>
      <Flex justify="space-between" gap={10}>
        <SearchInput placeholder="Search for flag..." value={flagSearch} onChange={onFlagSearchChange} />
        <Button
          size={BUTTON_SIZE.LARGE}
          type={BUTTON_TYPE.PRIMARY}
          isDisabled={isSaveDisabled}
          onClick={onSaveFlagsClick}
        >
          Save Flags
        </Button>
      </Flex>
      <ReactTable minRows={0} columns={featureFlagColumns} data={flagData} defaultPageSize={50} />
    </div>
  );
};

export default WithDialogContext<Omit<FeatureControlCardProps, 'dialogContext'>>(FeatureControlCard);
